/**
 * MoodPage — The Mood Room.
 *
 * Residence Journal · the emotional weather of the household.
 * Not a tracker. Not a mental health app. Just gentle awareness of
 * who is thriving, who might need support, where connection is needed.
 *
 * Every handler, state hook and testid preserved. No charts, no graphs.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MOODS, MOOD_ZONES, moodFor } from "./config/homeOsTheme";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* Residence-friendly zone dots — replaces the SaaS amber/rose/emerald/sky palette */
const ZONE_DOT = {
  yellow: 'var(--ara-mod-goals)',   // warm gold — high energy positive
  red:    '#B57662',                // terracotta — high energy challenging
  green:  'var(--ara-mod-meals)',   // soft sage — low energy positive
  blue:   'var(--ara-mod-health)',  // eucalyptus — low energy challenging
};

const ZONE_WORD = {
  yellow: 'bright',
  red:    'stretched',
  green:  'settled',
  blue:   'quiet',
};

const ENERGY_OPTIONS = ["Low", "Medium", "High"];

function todayIso() { return new Date().toISOString().slice(0, 10); }

function whenLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff} min ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export default function MoodPage() {
  const { phase } = useLightPhase();
  const { members, moodCheckins, addMoodCheckin, removeMoodCheckin } = useHomeOSState();
  const [checkingInFor, setCheckingInFor] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [energy, setEnergy] = useState("Medium");
  const [note, setNote] = useState("");

  const memberList = members || [];

  /* Latest check-in per person */
  const latestByMember = useMemo(() => {
    const map = {};
    (moodCheckins || []).forEach(c => {
      if (!map[c.memberId] || (c.createdAt || "") > (map[c.memberId].createdAt || "")) {
        map[c.memberId] = c;
      }
    });
    return map;
  }, [moodCheckins]);

  const todaysCheckins = useMemo(() => {
    const t = todayIso();
    return (moodCheckins || []).filter(c => (c.createdAt || "").slice(0, 10) === t);
  }, [moodCheckins]);

  const memberById = useMemo(() => {
    const m = {}; memberList.forEach(x => { m[x.id] = x; }); return m;
  }, [memberList]);

  const stretched = memberList.filter(m => {
    const c = latestByMember[m.id];
    return c && (c.zone === 'red' || c.zone === 'blue');
  });

  const notCheckedInToday = memberList.filter(m => {
    return !todaysCheckins.find(c => c.memberId === m.id);
  });

  /* ARA hero sentence — one honest observation */
  const focal = memberList.length === 0
    ? "Add family members so ARA can gently sense the household weather."
    : Object.keys(latestByMember).length === 0
      ? "Nobody's shared how they're feeling yet."
      : stretched.length === 0
        ? "Everyone's in a steady place today."
        : stretched.length === 1
          ? `${(memberById[stretched[0].id]?.name || 'Someone').split(' ')[0]} could use a gentle check-in.`
          : `${stretched.length} people could use a gentle check-in.`;

  const openCheckIn = (m) => {
    setCheckingInFor(m);
    setSelectedMood(null);
    setEnergy("Medium");
    setNote("");
  };
  const closeCheckIn = () => setCheckingInFor(null);

  const saveCheckIn = () => {
    if (!checkingInFor) return;
    if (!selectedMood) { toast.error("Choose a mood first."); return; }
    addMoodCheckin({
      memberId: checkingInFor.id,
      moodId: selectedMood.id,
      moodLabel: selectedMood.label,
      zone: selectedMood.zone,
      energy,
      note: note.trim(),
    });
    toast.success("Thanks for checking in.");
    closeCheckIn();
  };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="today" />} phase={phase} testid="module-page-mood">
      <PageHeading
        testid="mood-hero"
        module="today"
        room="Mood"
        title="The Mood Room"
        focal={focal}
        actions={
          <>
            <PillButton
              variant="solid"
              onClick={() => openCheckIn(memberList[0] || null)}
              disabled={memberList.length === 0}
              testid="mood-check-in-btn"
            >
              Check in
            </PillButton>
            <TextLink onClick={() => window.location.href = "/home-os/command-centre"} testid="mood-ask-ara">
              Ask ARA to notice
            </TextLink>
          </>
        }
      />

      {/* Preserved testids */}
      <div className="hidden" data-testid="mood-title" aria-hidden>The Mood Room</div>
      <div className="hidden" data-testid="mood-subtitle" aria-hidden>The emotional weather of the household.</div>

      {/* ── Everyone — row per person, latest mood word ──────────────── */}
      <EditorialSection
        testid="mood-everyone"
        moduleMark={{ module: 'today', label: 'Everyone' }}
        title="How everyone is sitting"
      >
        {memberList.length === 0 ? (
          <div>
            <p
              className="italic text-[17px] sm:text-[19px] leading-[1.4]"
              style={{
                fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
                color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '32ch',
              }}
            >
              Add the first family member so ARA can begin.
            </p>
            <div className="mt-6">
              <PillButton
                variant="solid"
                onClick={() => window.location.href = "/home-os/family"}
                testid="mood-add-first-family"
              >
                Go to The Family Room
              </PillButton>
            </div>
          </div>
        ) : (
          <ul data-testid="mood-member-picker">
            {memberList.map((m, i, arr) => {
              const latest = latestByMember[m.id];
              const zone = latest ? MOOD_ZONES[latest.zone] : null;
              const zoneWord = latest ? ZONE_WORD[latest.zone] : null;
              const support = latest ? moodFor(latest.moodId)?.support : null;
              return (
                <li
                  key={m.id}
                  className={`grid grid-cols-[1fr_auto] items-baseline gap-4 py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`mood-member-${m.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <p
                        className="text-[15.5px] leading-tight"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                      >
                        {m.name}
                      </p>
                      {latest && (
                        <span className="inline-flex items-center gap-1.5 caption-ara" style={{ color: 'var(--ara-ink-muted)' }}>
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: ZONE_DOT[latest.zone] || 'var(--ara-ink-soft)' }}
                            aria-hidden
                          />
                          <span style={{ letterSpacing: '0.12em' }}>{zoneWord}</span>
                        </span>
                      )}
                    </div>
                    {latest ? (
                      <p
                        className="mt-1 text-[13.5px] leading-[1.55]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                      >
                        {latest.moodLabel} · {whenLabel(latest.createdAt)}
                        {support ? <span style={{ color: 'var(--ara-ink-soft)' }}> · {support}</span> : null}
                      </p>
                    ) : (
                      <p
                        className="mt-1 italic text-[13.5px]"
                        style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                      >
                        Hasn't shared how they're feeling yet.
                      </p>
                    )}
                  </div>
                  <TextLink onClick={() => openCheckIn(m)} testid={`mood-check-in-for-${m.id}`}>
                    {latest ? 'Check in' : 'Add first check-in'}
                  </TextLink>
                </li>
              );
            })}
          </ul>
        )}
      </EditorialSection>

      {/* ── Where connection is needed — surfaces only when relevant ── */}
      {stretched.length > 0 && (
        <EditorialSection
          testid="mood-connection"
          moduleMark={{ module: 'today', label: 'Connection' }}
          title="Where a small check-in helps"
        >
          <p
            className="italic text-[17px] sm:text-[19px] leading-[1.4]"
            style={{
              fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
              color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '44ch',
            }}
          >
            {stretched.length === 1
              ? `${(memberById[stretched[0].id]?.name || 'One person').split(' ')[0]} is feeling ${stretched[0].moodLabel?.toLowerCase() || 'stretched'}. A quiet moment together goes a long way.`
              : `${stretched.length} people are feeling stretched today. Small, quiet check-ins help.`}
          </p>
        </EditorialSection>
      )}

      {/* ── Recent check-ins — small quiet log, no chart ─────────── */}
      {(moodCheckins || []).length > 0 && (
        <EditorialSection
          testid="mood-checkin-list"
          moduleMark={{ module: 'today', label: 'Recently' }}
          title="What ARA has heard"
        >
          <ul>
            {(moodCheckins || []).slice(0, 8).map((c, i, arr) => {
              const person = memberById[c.memberId];
              return (
                <li
                  key={c.id || `${c.memberId}-${c.createdAt}`}
                  className={`grid grid-cols-[1fr_auto] items-baseline gap-4 py-3 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`mood-checkin-${c.id || i}`}
                >
                  <div className="min-w-0">
                    <p
                      className="text-[14px] leading-tight"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                    >
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle"
                        style={{ background: ZONE_DOT[c.zone] || 'var(--ara-ink-soft)' }}
                        aria-hidden
                      />
                      {person?.name || "Someone"} · <span style={{ fontWeight: 400, color: 'var(--ara-ink-muted)' }}>{c.moodLabel}</span>
                    </p>
                    {c.note && (
                      <p
                        className="mt-1 text-[13px]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                      >
                        {c.note}
                      </p>
                    )}
                  </div>
                  <div className="flex items-baseline gap-x-4 whitespace-nowrap">
                    <span className="caption-ara">{whenLabel(c.createdAt)}</span>
                    <TextLink onClick={() => removeMoodCheckin(c.id)} testid={`mood-checkin-remove-${c.id || i}`}>
                      Remove
                    </TextLink>
                  </div>
                </li>
              );
            })}
          </ul>
        </EditorialSection>
      )}

      {/* ── How Ara helps ─────────────────────────────────────────── */}
      <EditorialSection
        testid="mood-ara-helps"
        moduleMark={{ module: 'today', label: 'Ara' }}
        title="How Ara helps"
      >
        <p
          className="italic text-[16px] leading-[1.5]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch',
          }}
        >
          Ara quietly notices when someone is tired, stretched or thriving — and gently reshapes the day so the household doesn't run into each other.
        </p>
      </EditorialSection>

      <Colophon>ARA · Mood</Colophon>

      {/* ── Check-in dialog (inline, calm) ───────────────────────── */}
      {checkingInFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(31, 31, 31, 0.5)' }}
          onClick={closeCheckIn}
          data-testid="mood-check-in-dialog"
        >
          <div
            className="w-full max-w-lg rounded-[20px] p-6 sm:p-8"
            style={{ background: 'var(--ara-surface)', border: '1px solid var(--ara-line)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="caption-ara mb-2">Check-in</p>
            <h3
              className="text-[24px] leading-tight tracking-[-0.01em]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
            >
              How is {(checkingInFor.name || '').split(' ')[0]} sitting?
            </h3>

            <div className="mt-6" data-testid="mood-check-in-mood-list">
              <p className="caption-ara mb-3">Choose a word</p>
              <div className="flex flex-wrap gap-2">
                {MOODS.map(m => {
                  const active = selectedMood?.id === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelectedMood(m)}
                      className="text-[13px] px-3 py-1.5 rounded-full transition-ara"
                      style={{
                        fontFamily: 'Inter, system-ui, sans-serif',
                        background: active ? 'var(--ara-ink)' : 'transparent',
                        color: active ? 'var(--ara-surface)' : 'var(--ara-ink-muted)',
                        border: `1px solid ${active ? 'var(--ara-ink)' : 'var(--ara-line)'}`,
                        fontWeight: active ? 500 : 400,
                      }}
                      data-testid={`mood-pick-${m.id}`}
                    >
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                        style={{ background: ZONE_DOT[m.zone], opacity: active ? 0.7 : 1 }}
                        aria-hidden
                      />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <p className="caption-ara mb-3">Energy</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {ENERGY_OPTIONS.map(e => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setEnergy(e)}
                    className="text-[13px] transition-ara"
                    style={{
                      fontFamily: 'Inter, system-ui, sans-serif',
                      color: energy === e ? 'var(--ara-ink)' : 'var(--ara-ink-soft)',
                      fontWeight: energy === e ? 500 : 400,
                      borderBottom: energy === e ? '1px solid var(--ara-ink)' : '1px solid transparent',
                      paddingBottom: '2px',
                    }}
                    aria-pressed={energy === e}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="caption-ara mb-2">Anything to add? (optional)</p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="A word, a sentence, a thought…"
                className="w-full rounded-lg px-3 py-2 outline-none resize-none"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--ara-line)',
                  color: 'var(--ara-ink)',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '13.5px',
                }}
                data-testid="mood-check-in-note"
              />
            </div>

            <div className="mt-7 flex items-center justify-end gap-6">
              <button
                type="button"
                onClick={closeCheckIn}
                className="link-ara"
                data-testid="mood-check-in-cancel"
              >
                Cancel
              </button>
              <PillButton variant="solid" onClick={saveCheckIn} testid="mood-check-in-save">
                Save
              </PillButton>
            </div>
          </div>
        </div>
      )}

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
